import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LogIn, Eye, EyeOff, UserPlus, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import useSystemVersion from "@/hooks/useSystemVersion";
import { BUILD_VERSION } from "@/lib/buildVersion";

export default function LoginPage() {
  const { login } = useAuth();
  const versionInfo = useSystemVersion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (err.response?.status === 422) {
        setError("דוא\"ל או סיסמה לא תקינים");
      } else if (!err.response) {
        // Network error / CORS / in-app browser blocking cookies
        setError("שגיאת תקשורת – נסה/י לפתוח את הקישור בדפדפן רגיל (Chrome/Safari) במקום בתוך וואטסאפ");
      } else {
        setError(`שגיאה בהתחברות (${err.response.status})`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" dir="rtl">
      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-stone-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center bg-primary">
              <span className="text-white text-2xl font-bold font-['Heebo']">CL</span>
            </div>
            <CardTitle className="text-3xl font-['Heebo'] font-bold text-primary">
              צהר הלב
            </CardTitle>
            {versionInfo.demo_mode ? (
              <div className="mt-2 inline-flex items-center justify-center rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800">
                מצב הדגמה
              </div>
            ) : null}
            <p className="text-sm mt-1 text-muted-foreground">טפסי הלב — מערכת הטפסים של צהר הלב</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-['Assistant'] text-sm text-muted-foreground">דוא"ל</Label>
                <Input
                   id="email"
                   data-testid="login-email-input"
                   type="email"
                   value={email}
                   onChange={e => setEmail(e.target.value)}
                   placeholder="admin@tzohar.co.il"
                   className="text-start bg-stone-50/50 border-stone-200 focus:border-primary focus:ring-primary"
                   required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="font-['Assistant'] text-sm text-muted-foreground">סיסמה</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="text-start bg-stone-50/50 border-stone-200 focus:border-primary focus:ring-primary pe-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute start-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              {error && <p data-testid="login-error" className="text-sm font-medium text-destructive">{error}</p>}
              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full text-white bg-primary hover:bg-primary-hover rounded-lg shadow-sm transition-all duration-200"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    מתחבר...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn size={18} />
                    התחברות
                  </span>
                )}
              </Button>
            </form>
            <div className="mt-5 pt-4 border-t border-stone-200 space-y-2">
              <Link
                to="/new"
                data-testid="login-new-employee-link"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-['Assistant'] text-primary border border-primary/30 hover:bg-primary/5 transition-colors"
              >
                <UserPlus size={16} />
                עובד/ת חדש/ה להזנת טופס — לחצ/י כאן
              </Link>
              <Link
                to="/change-details"
                data-testid="login-change-details-link"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-['Assistant'] text-primary border border-primary/30 hover:bg-primary/5 transition-colors"
              >
                <UserCheck size={16} />
                שינוי פרטים — לחצ/י כאן
              </Link>
            </div>
          </CardContent>
          {(versionInfo.label || BUILD_VERSION) ? (
            <div
              data-testid="login-version-badge"
              className="px-6 pb-5 pt-1 text-center text-sm font-bold font-['Assistant'] text-primary tracking-wide"
            >
              גרסה {versionInfo.label || BUILD_VERSION}
            </div>
          ) : null}
        </Card>
      </div>
      {/* Left side - decorative */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden bg-primary">
        <div className="text-center text-white p-12 z-10">
          <h1 className="text-5xl font-['Heebo'] font-bold mb-4">טפסי הלב</h1>
          <p className="text-xl font-['Assistant'] opacity-80 max-w-md">
            מערכת דיגיטלית למילוי, חתימה ושליחת הטפסים של צהר הלב
          </p>
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 start-20 w-64 h-64 rounded-full bg-success" />
          <div className="absolute bottom-10 end-10 w-96 h-96 rounded-full bg-primary-hover" />
        </div>
      </div>
    </div>
  );
}
